import { useCallback, useEffect, useRef, useState } from "react";
import { BrandMark } from "./TopBar";

/**
 * Стартовый экран: падающая линия (продажи) и растущая (покупки) рисуются навстречу и сходятся в точке разворота;
 * там появляется знак «ВнутриCOT» и название; затем знак переезжает на своё место в шапке. Около 2,9 с.
 *
 * Без зависимостей: CSS-переходы, полёт к логотипу — Web Animations API. Пропуск — клик, Esc, Enter или пробел.
 * При «уменьшить движение» в системе — короткое появление и затухание без полёта.
 * Отладка: ?intro=draw|mark|word|exit — застыть на фазе (клик или Esc — закрыть).
 */

/** Тайминги, мс от старта. */
const T = {
  draw: 60, // линии рисуются навстречу друг другу
  mark: 900, // в точке встречи появляется знак
  word: 1250, // название и подзаголовок
  exit: 2050, // текст уходит, знак летит в шапку
  fly: 2100,
  fade: 2550, // фон исчезает
  done: 2950,
};

type Phase = "init" | "draw" | "mark" | "word" | "exit" | "fade" | "skip";
const HOLD: Phase[] = ["draw", "mark", "word", "exit"];

export function Intro({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<Phase>("init");
  const markRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(false);
  const timers = useRef<number[]>([]);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    timers.current.forEach((t) => window.clearTimeout(t));
    onComplete();
  }, [onComplete]);

  const skip = useCallback(() => {
    if (doneRef.current) return;
    timers.current.forEach((t) => window.clearTimeout(t));
    setPhase("skip");
    timers.current = [window.setTimeout(finish, 300)];
  }, [finish]);

  // Знак переезжает в логотип шапки (тот же рисунок, меньший размер).
  const fly = useCallback(() => {
    const el = markRef.current;
    const logo = document.querySelector(".topbar .brand-mark");
    if (!el || !logo || typeof el.animate !== "function") return;
    const m = el.getBoundingClientRect();
    const l = logo.getBoundingClientRect();
    if (!m.width || !l.width) return;
    const s = l.width / m.width;
    const dx = l.left + l.width / 2 - (m.left + m.width / 2);
    const dy = l.top + l.height / 2 - (m.top + m.height / 2);
    el.animate(
      [{ transform: "translate(0, 0) scale(1)" }, { transform: `translate(${dx}px, ${dy}px) scale(${s})` }],
      { duration: 620, easing: "cubic-bezier(.6,0,.2,1)", fill: "forwards" },
    );
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const at = (ms: number, f: () => void) => timers.current.push(window.setTimeout(f, ms));
    const hold = new URLSearchParams(window.location.search).get("intro") as Phase | null;
    if (hold && HOLD.includes(hold)) {
      HOLD.slice(0, HOLD.indexOf(hold) + 1).forEach((ph, k) => at(40 + k * 700, () => setPhase(ph)));
      if (hold === "exit") at(40 + 3 * 700 + 60, fly);
    } else if (reduce) {
      at(30, () => setPhase("word"));
      at(900, () => setPhase("skip"));
      at(1200, finish);
    } else {
      at(T.draw, () => setPhase("draw"));
      at(T.mark, () => setPhase("mark"));
      at(T.word, () => setPhase("word"));
      at(T.exit, () => setPhase("exit"));
      at(T.fly, fly);
      at(T.fade, () => setPhase("fade"));
      at(T.done, finish);
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") skip();
    };
    window.addEventListener("keydown", onKey);
    const list = timers.current;
    return () => {
      list.forEach((t) => window.clearTimeout(t));
      window.removeEventListener("keydown", onKey);
    };
  }, [finish, fly, skip]);

  return (
    <div className="intro" data-phase={phase} onClick={skip} role="presentation">
      <div className="intro-bg" aria-hidden="true" />
      <div className="intro-stage" aria-hidden="true">
        <svg className="intro-lines" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid meet">
          <path className="intro-axis" d="M0 200 H1000" />
          {/* продажи: сверху слева вниз к центру */}
          <path
            className="intro-line intro-line-bear"
            pathLength={1}
            d="M20 70 L90 96 L150 82 L215 128 L270 116 L330 158 L385 146 L440 184 L500 200"
          />
          {/* покупки: от точки встречи вверх вправо — разворот у экстремума */}
          <path
            className="intro-line intro-line-bull"
            pathLength={1}
            d="M980 70 L910 96 L850 82 L785 128 L730 116 L670 158 L615 146 L560 184 L500 200"
          />
        </svg>
        <div className="intro-center">
          <div className="intro-mark" ref={markRef}>
            <BrandMark size={84} className="intro-brand-mark" />
          </div>
          <p className="intro-title">
            Внутри<b>COT</b><span>Изучаем позиции крупных игроков на российском и американском рынке</span>
          </p>
        </div>
      </div>
      <h1 className="visually-hidden">ВнутриCOT</h1>
      <button
        type="button"
        className="intro-skip"
        onClick={(e) => {
          e.stopPropagation();
          skip();
        }}
      >
        Пропустить
      </button>
    </div>
  );
}
